import { Component, Input, Output, EventEmitter, OnChanges, OnInit, SimpleChanges, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OrcamentoService } from '../../../../services/orcamento.service';
import { ToastService } from 'src/app/shared/toasts/toast-service';
import { ActivatedRoute, Router } from '@angular/router';
import { Orcamento } from '../../../../models/orcamento.model';
import { calcularMetrosQuadrados } from 'src/app/core/utils/calculo-quadrados.utils';
import { StatusOrcamentoEnum } from '../../../../models/status-orcamento.enum';
import { BudgetService } from '../../../../services/budget.service';
import { ProjetoFeiraService } from '../../../../services/projeto-feira.service';
import { Projeto } from '../../../../models/project.model';
import { FiltrarProjeto } from '../../../../models/project-filter.model';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CriarOrcamentoDto } from '../../../../dtos/criar-orcamento.dto';


@Component({
  selector: 'app-form-parametro-orcamento',
  templateUrl: './form-parametro-orcamento.component.html',
  styleUrl: './form-parametro-orcamento.component.scss'
})
export class FormParametroOrcamentoComponent implements OnInit, OnChanges, OnDestroy {
  @Input() orcamento!: Orcamento | null;
  @Output() orcamentoChanged = new EventEmitter<Orcamento>();

  parametroForm!: FormGroup;

  loading: boolean = false;

  totalMetrosQuadrados: number = 0;
  totalMetrosSobrepor: number = 0;
  orcamentoId? = '';
  private isUpdatingFromParent = false; // Flag para evitar loop infinito

  // Projetos com scroll infinito
  projetosFiltrados: Projeto[] = [];
  buscaProjeto: string = '';
  dropdownProjetoAberto: boolean = false;
  projetoSelecionado: Projeto | null = null;
  projetoPaginas: { paginaAtual: number; totalPaginas: number; carregandoMais: boolean; temMais: boolean } = {
    paginaAtual: 1,
    totalPaginas: 1,
    carregandoMais: false,
    temMais: true
  };
  buscaProjetoDebounce$ = new Subject<string>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private orcamentoService: OrcamentoService,
    private budgetService: BudgetService,
    private fb: FormBuilder,
    private location: Location,
    private toastService: ToastService,
    private projetoFeiraService: ProjetoFeiraService
  ) { }

  ngOnInit(): void {
    this.orcamentoId = this.route.snapshot.paramMap.get('id') || '';

    this.iniciaParametroForm(this.orcamentoId);

    this.parametroForm.valueChanges.subscribe(() => {
      // Se está atualizando do componente pai, não emite para evitar loop
      if (this.isUpdatingFromParent) {
        return;
      }
      
      this.calcularMetroQuadrado();
      this.calcularMetroQuadradoSobrepor();
      
      // Emite as mudanças para o componente pai manter o orçamento sincronizado
      if (this.parametroForm.valid || this.parametroForm.value.comprimento || this.parametroForm.value.largura) {
        this.emitirOrcamentoAtualizado();
      }
    });

    // Configurar busca de projetos com debounce
    this.buscaProjetoDebounce$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(termo => {
      this.carregarProjetos(true);
    });

    // Se o orcamento já tiver valores, preenche o formulário
    if (this.orcamento) {
      this.preencherFormularioComOrcamento(this.orcamento);
    } else {
      // Carregar projetos inicialmente
      this.carregarProjetos(true);
    }
  }

  ngOnDestroy(): void {
    this.buscaProjetoDebounce$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orcamento'] && changes['orcamento'].currentValue) {
      if (this.parametroForm) {
        // Salvar o statusOrcamento atual antes de preencher o formulário
        const statusOrcamentoAtual = this.parametroForm.get('statusOrcamento')?.value;
        this.preencherFormularioComOrcamento(this.orcamento!);
        // Restaurar o statusOrcamento se foi alterado pelo usuário (não veio da API)
        if (statusOrcamentoAtual !== undefined && statusOrcamentoAtual !== null) {
          const statusOrcamentoDaApi = (this.orcamento as any)?.statusOrcamento;
          // Se o status da API é diferente do atual, significa que o usuário alterou
          // Manter o valor que o usuário selecionou
          if (statusOrcamentoDaApi === undefined || statusOrcamentoDaApi === null || 
              Number(statusOrcamentoDaApi) !== Number(statusOrcamentoAtual)) {
            setTimeout(() => {
              this.parametroForm.get('statusOrcamento')?.setValue(statusOrcamentoAtual, { emitEvent: false });
            }, 0);
          }
        }
      }
    }
  }

  /**
   * Preenche o formulário com os dados do orçamento
   * Faz mapeamento de campos caso a API retorne com nomes diferentes
   */
  private preencherFormularioComOrcamento(orcamento: Orcamento): void {
    if (!this.parametroForm) {
      // Se o formulário ainda não foi inicializado, aguarda um pouco
      setTimeout(() => {
        if (this.parametroForm) {
          this.preencherFormularioComOrcamento(orcamento);
        }
      }, 100);
      return;
    }

    // Mapear campos da API para o formulário
    // A API pode retornar nomeFeira em vez de feira, ou projetoFeiraId em vez de projetoId
    const orcamentoApi: any = orcamento;
    
    // Função auxiliar para obter valor, verificando múltiplas propriedades possíveis
    const obterValor = (...valores: any[]): any => {
      for (const valor of valores) {
        if (valor !== null && valor !== undefined && valor !== '') {
          return valor;
        }
      }
      return '';
    };
    
    // Priorizar projetoFeiraId quando disponível (vem da API)
    const projetoIdParaUsar = obterValor(
      orcamentoApi.projetoFeiraId,  // Prioridade 1: projetoFeiraId da API
      orcamento.projetoId,           // Prioridade 2: projetoId do modelo
      orcamentoApi.projetoId,        // Prioridade 3: projetoId da API
      ''
    );
    
    // Obter statusOrcamento da API (pode vir como statusOrcamento)
    // Se não vier, usar o valor padrão do formulário (não sobrescrever)
    const statusOrcamentoDaApi = orcamentoApi.statusOrcamento !== undefined && orcamentoApi.statusOrcamento !== null
      ? Number(orcamentoApi.statusOrcamento)
      : null;
    
    const valoresFormulario: any = {
      id: obterValor(orcamento.id, this.orcamentoId, ''),
      descricao: obterValor(orcamento.descricao, orcamentoApi.descricao, ''),
      projetoId: projetoIdParaUsar,
      feira: obterValor(orcamento.feira, orcamentoApi.feira, orcamentoApi.nomeFeira, ''),
      // Só atualizar statusOrcamento se vier da API, caso contrário manter o valor do formulário
      ...(statusOrcamentoDaApi !== null && { statusOrcamento: statusOrcamentoDaApi }),
      comprimento: orcamento.comprimento ?? 0,
      largura: orcamento.largura ?? 0,
      comprimentoSobrepor: orcamento.comprimentoSobrepor ?? 0,
      larguraSobrepor: orcamento.larguraSobrepor ?? 0,
      percentualImpostoFaturaIsentoIss: orcamento.percentualImpostoFaturaIsentoIss ?? 0,
      percentualCustoOperacional: orcamento.percentualCustoOperacional ?? 0,
      percentualMargemDeLucro: orcamento.percentualMargemDeLucro ?? 0,
      percentualVariacaoOrcamento: orcamento.percentualVariacaoOrcamento ?? 0,
      percentualComissaoVendas: orcamento.percentualComissaoVendas ?? 0,
      percentualStudioArquiteto: orcamento.percentualStudioArquiteto ?? 0,
      percentualMargemNegociacao: orcamento.percentualMargemNegociacao ?? 0
    };

    // Atualiza o orcamentoId se tiver ID
    if (orcamento.id) {
      this.orcamentoId = orcamento.id;
      valoresFormulario.id = orcamento.id;
    }

    // Marca que está atualizando do componente pai para evitar loop
    this.isUpdatingFromParent = true;
    
    // Preenche o formulário com todos os valores
    this.parametroForm.patchValue(valoresFormulario, { emitEvent: false });
    
    // Se tiver projetoId (ou projetoFeiraId), apenas preencher o campo
    // O projeto será carregado quando o usuário abrir o dropdown
    if (projetoIdParaUsar) {
      // Apenas preencher o ID no formulário, sem fazer requisição
      // O projeto será buscado quando o usuário interagir com o campo
      this.parametroForm.patchValue({ projetoId: projetoIdParaUsar }, { emitEvent: false });
    }
    
    // Garantir que o statusOrcamento seja atualizado corretamente se vier da API
    if (statusOrcamentoDaApi !== null) {
      this.parametroForm.get('statusOrcamento')?.setValue(statusOrcamentoDaApi, { emitEvent: false });
    }
    
    // Reseta o flag após um pequeno delay para permitir que o patchValue complete
    setTimeout(() => {
      this.isUpdatingFromParent = false;
    }, 100);
  }

  iniciaParametroForm(orcamentoid: string): void {

    this.parametroForm = this.fb.group({
      id: [orcamentoid],
      descricao: ['', Validators.required],
      projetoId: [''],
      feira: ['', Validators.required],
      statusOrcamento: [StatusOrcamentoEnum.Orcando],
      comprimento: ['', Validators.required],
      largura: ['', Validators.required],
      comprimentoSobrepor: ['', Validators.required],
      larguraSobrepor: ['', Validators.required],
      percentualImpostoFaturaIsentoIss: ['', Validators.required],
      percentualCustoOperacional: ['', Validators.required],
      percentualMargemDeLucro: ['', Validators.required],
      percentualVariacaoOrcamento: ['', Validators.required],
      percentualComissaoVendas: ['', Validators.required],
      percentualStudioArquiteto: ['', Validators.required],
      percentualMargemNegociacao: ['', Validators.required]
    });
  }

  onSubmit(): void {
    if (this.parametroForm.invalid) {
      this.parametroForm.markAllAsTouched();
      this.toastService.show("Por favor, preencha todos os campos obrigatórios", {
        classname: "bg-warning text-light",
      });
      return;
    }

    this.loading = true;
    const formValue = this.parametroForm.value;
    const orcamentoId = formValue.id || this.orcamentoId;

    // Função auxiliar para converter valores de string para número
    const parseValue = (value: any): number => {
      if (!value || value === '') return 0;
      if (typeof value === 'number') return value;
      // Remove pontos de milhar e substitui vírgula por ponto
      const str = String(value).replace(/\./g, '').replace(',', '.');
      return parseFloat(str) || 0;
    };

    // Se não tem ID, é criação - usar POST
    if (!orcamentoId || orcamentoId === '' || orcamentoId === 'novo') {
      const criarOrcamentoDto: CriarOrcamentoDto = {
        projetoFeiraId: formValue.projetoId || undefined, // Opcional
        nomeFeira: formValue.feira || '',
        descricao: formValue.descricao || '',
        comprimento: parseValue(formValue.comprimento),
        largura: parseValue(formValue.largura),
        comprimentoSobrepor: parseValue(formValue.comprimentoSobrepor),
        larguraSobrepor: parseValue(formValue.larguraSobrepor),
        percentualImpostoFaturaIsentoIss: parseValue(formValue.percentualImpostoFaturaIsentoIss),
        percentualCustoOperacional: parseValue(formValue.percentualCustoOperacional),
        percentualMargemDeLucro: parseValue(formValue.percentualMargemDeLucro),
        percentualVariacaoOrcamento: parseValue(formValue.percentualVariacaoOrcamento),
        percentualComissaoVendas: parseValue(formValue.percentualComissaoVendas),
        percentualStudioArquiteto: parseValue(formValue.percentualStudioArquiteto),
        percentualMargemNegociacao: parseValue(formValue.percentualMargemNegociacao)
      };

      // Remover projetoFeiraId se estiver vazio
      if (!criarOrcamentoDto.projetoFeiraId) {
        delete criarOrcamentoDto.projetoFeiraId;
      }

      this.orcamentoService.criarOrcamento(criarOrcamentoDto).subscribe({
        next: (response: any) => {
          // Atualiza o ID do orçamento criado
          const responseId = response?.id || (response as any)?.id;
          if (responseId) {
            this.parametroForm.patchValue({ id: responseId });
            this.orcamentoId = responseId;
            
            // Atualiza o orçamento local com o ID retornado
            const orcamentoAtualizado: any = {
              id: responseId,
              descricao: formValue.descricao || '',
              projetoId: formValue.projetoId || '',
              feira: formValue.feira || '',
              comprimento: criarOrcamentoDto.comprimento,
              largura: criarOrcamentoDto.largura,
              comprimentoSobrepor: criarOrcamentoDto.comprimentoSobrepor,
              larguraSobrepor: criarOrcamentoDto.larguraSobrepor,
              percentualImpostoFaturaIsentoIss: criarOrcamentoDto.percentualImpostoFaturaIsentoIss,
              percentualCustoOperacional: criarOrcamentoDto.percentualCustoOperacional,
              percentualMargemDeLucro: criarOrcamentoDto.percentualMargemDeLucro,
              percentualVariacaoOrcamento: criarOrcamentoDto.percentualVariacaoOrcamento,
              percentualComissaoVendas: criarOrcamentoDto.percentualComissaoVendas,
              percentualStudioArquiteto: criarOrcamentoDto.percentualStudioArquiteto,
              percentualMargemNegociacao: criarOrcamentoDto.percentualMargemNegociacao
            };
            
            // Emite o orçamento criado para o componente pai
            this.orcamentoChanged.emit(orcamentoAtualizado);
            
            // Navega para a rota com o ID do orçamento criado
            this.router.navigate(['/telemarketing/projetos/orcamento/' + responseId], {
              replaceUrl: true // Substitui a rota atual no histórico, evitando voltar para /novo
            });
          }
          
          this.toastService.show("Orçamento criado com sucesso", {
            classname: "bg-success text-light",
          });
        },
        error: (error) => {
          console.error('Erro ao criar orçamento:', error);
          this.toastService.show("Erro ao criar orçamento", {
            classname: "bg-danger text-light",
          });
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else {
      // Se tem ID, é edição - usar PUT (método existente)
      const parametros: any = {
        id: orcamentoId,
        projetoFeiraId: this.projetoSelecionado?.id || null,
        descricao: formValue.descricao || '',
        projetoId: formValue.projetoId || '',
        feira: formValue.feira || '',
        statusOrcamento: formValue.statusOrcamento !== undefined && formValue.statusOrcamento !== null 
          ? Number(formValue.statusOrcamento) 
          : StatusOrcamentoEnum.Orcando,
        comprimento: parseValue(formValue.comprimento),
        largura: parseValue(formValue.largura),
        comprimentoSobrepor: parseValue(formValue.comprimentoSobrepor),
        larguraSobrepor: parseValue(formValue.larguraSobrepor),
        percentualImpostoFaturaIsentoIss: parseValue(formValue.percentualImpostoFaturaIsentoIss),
        percentualCustoOperacional: parseValue(formValue.percentualCustoOperacional),
        percentualMargemDeLucro: parseValue(formValue.percentualMargemDeLucro),
        percentualVariacaoOrcamento: parseValue(formValue.percentualVariacaoOrcamento),
        percentualComissaoVendas: parseValue(formValue.percentualComissaoVendas),
        percentualStudioArquiteto: parseValue(formValue.percentualStudioArquiteto),
        percentualMargemNegociacao: parseValue(formValue.percentualMargemNegociacao)
      };

      this.orcamentoService.registrarParametro(parametros).subscribe({
        next: (response: any) => {
          // Emite o orçamento atualizado após salvar
          this.orcamentoChanged.emit(parametros);
          
          this.toastService.show("Parâmetros atualizados com sucesso", {
            classname: "bg-success text-light",
          });
        },
        error: () => {
          this.toastService.show("Erro ao atualizar parâmetros", {
            classname: "bg-danger text-light",
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    }
  }

  calcularMetroQuadrado(): void {
    let comprimentoRaw = this.parametroForm.get('comprimento')?.value ?? '0';
    let larguraRaw = this.parametroForm.get('largura')?.value ?? '0';
    this.totalMetrosQuadrados = calcularMetrosQuadrados(comprimentoRaw, larguraRaw);
  }

  calcularMetroQuadradoSobrepor(): void {
    let comprimentoRaw = this.parametroForm.get('comprimentoSobrepor')?.value ?? '0';
    let larguraRaw = this.parametroForm.get('larguraSobrepor')?.value ?? '0';
    this.totalMetrosSobrepor = calcularMetrosQuadrados(comprimentoRaw, larguraRaw);
  }

  // Emite os valores do formulário para o componente pai manter o orçamento sincronizado
  private emitirOrcamentoAtualizado(): void {
    if (!this.parametroForm) return;
    
    const formValue = this.parametroForm.value;
    
    // Converte valores de string para número (tratando máscaras de entrada)
    const parseValue = (value: any): number => {
      if (!value || value === '') return 0;
      if (typeof value === 'number') return value;
      // Remove pontos de milhar e substitui vírgula por ponto
      const str = String(value).replace(/\./g, '').replace(',', '.');
      return parseFloat(str) || 0;
    };
    
    const orcamentoAtualizado: any = {
      ...(this.orcamento || {}),
      id: formValue.id || this.orcamento?.id,
      descricao: formValue.descricao || '',
      projetoId: formValue.projetoId || '',
      feira: formValue.feira || '',
      statusOrcamento: formValue.statusOrcamento !== undefined && formValue.statusOrcamento !== null 
        ? Number(formValue.statusOrcamento) 
        : StatusOrcamentoEnum.Orcando,
      comprimento: parseValue(formValue.comprimento),
      largura: parseValue(formValue.largura),
      comprimentoSobrepor: parseValue(formValue.comprimentoSobrepor),
      larguraSobrepor: parseValue(formValue.larguraSobrepor),
      percentualImpostoFaturaIsentoIss: parseValue(formValue.percentualImpostoFaturaIsentoIss),
      percentualCustoOperacional: parseValue(formValue.percentualCustoOperacional),
      percentualMargemDeLucro: parseValue(formValue.percentualMargemDeLucro),
      percentualVariacaoOrcamento: parseValue(formValue.percentualVariacaoOrcamento),
      percentualComissaoVendas: parseValue(formValue.percentualComissaoVendas),
      percentualStudioArquiteto: parseValue(formValue.percentualStudioArquiteto),
      percentualMargemNegociacao: parseValue(formValue.percentualMargemNegociacao)
    };
    
    this.orcamentoChanged.emit(orcamentoAtualizado);
  }

  /// Baixar arquivo Excel
  baixarArquivoExcel(): void {

    this.budgetService.baixarExcelOrcamento(this.orcamentoId!).subscribe({
      next: (blob) => {
        const link = document.createElement('a');
        const url = window.URL.createObjectURL(blob);

        link.href = url;
        link.download = 'orcamento.xlsx';
        link.click();

        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Erro ao baixar arquivo:', err);
      }
    });
  }

  voltar() {
    this.location.back();
  }

  // ========== MÉTODOS PARA PROJETOS ==========
  
  abrirDropdownProjeto(): void {
    this.dropdownProjetoAberto = true;
    
    // Se não há projetos filtrados, carregar
    if (this.projetosFiltrados.length === 0) {
      this.carregarProjetos(true);
    }
    
    // Se há um projetoId no formulário mas não há projeto selecionado, tentar encontrar na lista
    const projetoId = this.parametroForm.get('projetoId')?.value;
    if (projetoId && !this.projetoSelecionado) {
      // Verificar se o projeto já está na lista filtrada
      const projetoNaLista = this.projetosFiltrados.find(p => p.id === projetoId);
      if (projetoNaLista) {
        this.buscaProjeto = `${projetoNaLista.descricao} - ${projetoNaLista.cliente}`;
        this.projetoSelecionado = projetoNaLista;
      }
    }
  }

  fecharDropdownProjeto(): void {
    setTimeout(() => {
      this.dropdownProjetoAberto = false;
    }, 200);
  }

  selecionarProjeto(projeto: Projeto): void {
    this.parametroForm.patchValue({ projetoId: projeto.id });
    this.buscaProjeto = `${projeto.descricao} - ${projeto.cliente}`;
    this.projetoSelecionado = projeto;
    this.dropdownProjetoAberto = false;
    
    // Emite mudanças
    if (!this.isUpdatingFromParent) {
      this.emitirOrcamentoAtualizado();
    }
  }

  carregarProjetos(resetar: boolean = true): void {
    if (resetar) {
      this.projetoPaginas = {
        paginaAtual: 1,
        totalPaginas: 1,
        carregandoMais: false,
        temMais: true
      };
      this.projetosFiltrados = [];
    }
    
    if (this.projetoPaginas.carregandoMais || !this.projetoPaginas.temMais) {
      return;
    }
    
    const termo = this.buscaProjeto || '';
    
    const filtros: FiltrarProjeto = {
      paginaAtual: this.projetoPaginas.paginaAtual.toString(),
      quantidadePorPagina: '20',
      status: [0,1],
      cliente: termo,
      feira: ''
    };
    
    if (resetar) {
      this.projetoPaginas.carregandoMais = true;
    }
    
    this.projetoFeiraService.consultarProjetosEmAndamento(filtros).subscribe({
      next: (response) => {
        const novosProjetos = response.projetos || [];
        
        if (resetar) {
          this.projetosFiltrados = novosProjetos;
        } else {
          this.projetosFiltrados = [...this.projetosFiltrados, ...novosProjetos];
        }
        
        // Se há um projetoId no formulário mas não há projeto selecionado, tentar encontrar na lista recém carregada
        const projetoId = this.parametroForm.get('projetoId')?.value;
        if (projetoId && !this.projetoSelecionado) {
          const projetoEncontrado = novosProjetos.find(p => p.id === projetoId);
          if (projetoEncontrado) {
            this.buscaProjeto = `${projetoEncontrado.descricao} - ${projetoEncontrado.cliente}`;
            this.projetoSelecionado = projetoEncontrado;
          }
        }
        
        this.projetoPaginas.totalPaginas = response.paginacao?.ultimaPagina || 1;
        this.projetoPaginas.temMais = this.projetoPaginas.paginaAtual < this.projetoPaginas.totalPaginas;
        this.projetoPaginas.carregandoMais = false;
      },
      error: (error) => {
        console.error('Erro ao carregar projetos:', error);
        this.toastService.show('Erro ao carregar projetos', {
          classname: 'bg-danger text-light',
        });
        this.projetoPaginas.carregandoMais = false;
      }
    });
  }

  carregarMaisProjetos(): void {
    this.projetoPaginas.paginaAtual++;
    this.carregarProjetos(false);
  }

  onScrollProjetos(event: Event): void {
    const element = event.target as HTMLElement;
    const scrollBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    
    if (scrollBottom < 50 && this.projetoPaginas.temMais && !this.projetoPaginas.carregandoMais) {
      this.carregarMaisProjetos();
    }
  }

  carregarProjetoSelecionado(projetoId: string): void {
    if (!projetoId) {
      return;
    }
    
    // Buscar o projeto específico para exibir no campo de busca
    // Usar uma quantidade maior para garantir que encontre o projeto
    const filtros: FiltrarProjeto = {
      paginaAtual: '1',
      quantidadePorPagina: '500', // Aumentar para garantir que encontre
      status: [0,1],
      cliente: '',
      feira: ''
    };
    
    this.projetoFeiraService.consultarProjetosEmAndamento(filtros).subscribe({
      next: (response) => {
        const projeto = response.projetos?.find(p => p.id === projetoId);
        if (projeto) {
          this.buscaProjeto = `${projeto.descricao} - ${projeto.cliente}`;
          this.projetoSelecionado = projeto;
          
          // Adicionar o projeto à lista de projetos filtrados se não estiver lá
          const projetoJaExiste = this.projetosFiltrados?.some(p => p.id === projetoId);
          if (!projetoJaExiste) {
            this.projetosFiltrados = [projeto, ...(this.projetosFiltrados || [])];
          }
          
          // Garantir que o formulário tenha o projetoId
          this.parametroForm.patchValue({ projetoId: projetoId }, { emitEvent: false });
        } else {
          // Se não encontrou na primeira página, tentar buscar em outras páginas
          // ou usar projetos realizados como fallback
          const filtrosRealizados: FiltrarProjeto = {
            paginaAtual: '1',
            quantidadePorPagina: '500',
            status: [3],
            cliente: '',
            feira: ''
          };
          
          this.projetoFeiraService.consultarProjetosRealizados(filtrosRealizados).subscribe({
            next: (responseRealizados) => {
              const projetoRealizado = responseRealizados.projetos?.find(p => p.id === projetoId);
              if (projetoRealizado) {
                this.buscaProjeto = `${projetoRealizado.descricao} - ${projetoRealizado.cliente}`;
                this.projetoSelecionado = projetoRealizado;
                
                // Adicionar o projeto à lista de projetos filtrados se não estiver lá
                const projetoJaExiste = this.projetosFiltrados?.some(p => p.id === projetoId);
                if (!projetoJaExiste) {
                  this.projetosFiltrados = [projetoRealizado, ...(this.projetosFiltrados || [])];
                }
                
                // Garantir que o formulário tenha o projetoId
                this.parametroForm.patchValue({ projetoId: projetoId }, { emitEvent: false });
              } else {
                // Se ainda não encontrou, pelo menos preencher o ID no formulário
                // e tentar buscar quando o usuário abrir o dropdown
                this.parametroForm.patchValue({ projetoId: projetoId }, { emitEvent: false });
                console.warn(`Projeto com ID ${projetoId} não encontrado nas listas disponíveis`);
              }
            },
            error: (errorRealizados) => {
              console.error('Erro ao buscar projeto em realizados:', errorRealizados);
              // Mesmo com erro, preencher o ID no formulário
              this.parametroForm.patchValue({ projetoId: projetoId }, { emitEvent: false });
            }
          });
        }
      },
      error: (error) => {
        console.error('Erro ao carregar projeto selecionado:', error);
        // Mesmo com erro, preencher o ID no formulário
        this.parametroForm.patchValue({ projetoId: projetoId }, { emitEvent: false });
      }
    });
  }
}
